
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { convertUnitToEnum } from '../utils/unitConversion';
import { calculateBulkPricing } from '../utils/bulkPricing';
import { ValidUnitValue } from '@/types/unit-types';

export const useLineItemMutations = (estimateId: string, lineItems: any[]) => {
  const queryClient = useQueryClient();

  // Add line item to estimate (with duplicate checking)
  const addLineItemMutation = useMutation({
    mutationFn: async ({ item, componentType }: { item: any; componentType: string }) => {
      console.log('[useLineItemMutations] Adding line item to estimate:', { item, componentType });
      
      // Check if item already exists in the estimate
      const { data: existingItems, error: checkError } = await supabase
        .from('estimate_line_items')
        .select('*')
        .eq('estimate_id', estimateId)
        .eq('component_id', item.id)
        .eq('component_type', componentType);

      if (checkError) {
        console.error('[useLineItemMutations] Error checking for existing items:', checkError);
        throw checkError;
      }

      // If item already exists, update its quantity instead of creating new
      if (existingItems && existingItems.length > 0) {
        const existingItem = existingItems[0];
        const newQuantity = existingItem.quantity_in_base_units + 1;
        
        console.log('[useLineItemMutations] Item already exists, updating quantity:', {
          existingItemId: existingItem.id,
          currentQuantity: existingItem.quantity_in_base_units,
          newQuantity
        });

        // Calculate new costs based on updated quantity
        let unitCost = existingItem.unit_cost_snapshot;
        
        // Recalculate unit cost if it was calculated with markup
        if (componentType === 'internal_line_item' || componentType === 'supplier_line_item') {
          unitCost = 
            typeof item.final_price === 'number'
              ? item.final_price
              : item.base_cost && typeof item.markup_percentage === 'number'
              ? item.base_cost * (1 + item.markup_percentage / 100)
              : item.base_cost || unitCost;
        }

        const { data: updatedItem, error: updateError } = await supabase
          .from('estimate_line_items')
          .update({
            quantity_in_base_units: newQuantity,
            unit_cost_snapshot: unitCost,
            total_cost_snapshot: unitCost * newQuantity,
          })
          .eq('id', existingItem.id)
          .select()
          .single();

        if (updateError) {
          console.error('[useLineItemMutations] Error updating existing item quantity:', updateError);
          throw updateError;
        }

        console.log('[useLineItemMutations] Existing item quantity updated successfully:', updatedItem);
        return { ...updatedItem, isUpdate: true };
      }
      
      let description = '';
      let baseUnit: ValidUnitValue = 'item';
      let unitCost = 0;

      if (componentType === 'internal_line_item') {
        description = item.name || item.title || item.description;
        baseUnit = convertUnitToEnum(item.base_unit || item.unit_symbol || 'item');
        unitCost =
          typeof item.final_price === 'number'
            ? item.final_price
            : item.base_cost && typeof item.markup_percentage === 'number'
            ? item.base_cost * (1 + item.markup_percentage / 100)
            : item.base_cost || 0;
      } else if (componentType === 'supplier_line_item') {
        description = item.name || item.title || item.description;
        baseUnit = convertUnitToEnum(item.base_unit || item.unit_symbol || 'item');
        unitCost =
          typeof item.final_price === 'number'
            ? item.final_price
            : item.base_cost && typeof item.markup_percentage === 'number'
            ? item.base_cost * (1 + item.markup_percentage / 100)
            : item.base_cost || 0;
      } else if (componentType === 'assembly') {
        description = item.name || item.description;
        baseUnit = 'item';
        unitCost = 0; // Assembly cost handled separately
      } else if (componentType === 'system_line_item') {
        description = item.name || item.description;
        baseUnit = convertUnitToEnum(item.unit_symbol || item.base_unit || 'item');
        unitCost =
          typeof item.final_price === 'number'
            ? item.final_price
            : item.base_cost || 0;
      }

      console.log('[useLineItemMutations] Processed line item data:', {
        description,
        baseUnit,
        unitCost,
        originalUnit: item.base_unit
      });
      
      const { data, error } = await supabase
        .from('estimate_line_items')
        .insert({
          estimate_id: estimateId,
          component_type: componentType,
          component_id: item.id,
          description: description,
          base_unit: baseUnit,
          quantity_in_base_units: 1,
          unit_cost_snapshot: unitCost,
          total_cost_snapshot: unitCost,
          sort_order: (lineItems?.length || 0) + 1,
          ...(componentType === 'system_line_item' ? {
            system_line_item_id: item.id,
            system_item_base_cost_at_add: item.base_cost ?? null,
          } : {}),
        })
        .select()
        .single();

      if (error) {
        console.error('[useLineItemMutations] Error adding line item:', error);
        throw error;
      }
      
      console.log('[useLineItemMutations] Line item added successfully:', data);
      return data;
    },
    onSuccess: (data: any) => {
      if (data && typeof data === 'object' && 'isUpdate' in data && data.isUpdate) {
        toast.success('Item quantity updated in estimate');
      } else {
        toast.success('Line item added to estimate');
      }
      queryClient.invalidateQueries({ queryKey: ['estimateLineItems', estimateId] });
    },
    onError: (error) => {
      console.error('[useLineItemMutations] Failed to add line item:', error);
      toast.error('Failed to add line item');
    },
  });

  // Remove line item from estimate
  const removeLineItemMutation = useMutation({
    mutationFn: async (lineItemId: string) => {
      console.log('[useLineItemMutations] Removing line item:', lineItemId);
      
      const { error } = await supabase
        .from('estimate_line_items')
        .delete()
        .eq('id', lineItemId);

      if (error) {
        console.error('[useLineItemMutations] Error removing line item:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast.success('Line item removed from estimate');
      queryClient.invalidateQueries({ queryKey: ['estimateLineItems', estimateId] });
    },
    onError: (error) => {
      console.error('[useLineItemMutations] Failed to remove line item:', error);
      toast.error('Failed to remove line item');
    },
  });

  // Update line item quantity mutation
  const updateQuantityMutation = useMutation({
    mutationFn: async ({ lineItemId, quantity, componentType, componentId, baseCost }: { 
      lineItemId: string; 
      quantity: number;
      componentType: string;
      componentId: string;
      baseCost: number;
    }) => {
      console.log('[useLineItemMutations] Updating quantity for line item:', { lineItemId, quantity });
      
      // Calculate bulk pricing
      const pricingResult = await calculateBulkPricing(componentType, componentId, quantity, baseCost);
      
      const { data, error } = await supabase
        .from('estimate_line_items')
        .update({
          quantity_in_base_units: quantity,
          unit_cost_snapshot: pricingResult.unitCost,
          total_cost_snapshot: pricingResult.totalCost,
        })
        .eq('id', lineItemId)
        .select()
        .single();

      if (error) {
        console.error('[useLineItemMutations] Error updating quantity:', error);
        throw error;
      }
      
      console.log('[useLineItemMutations] Quantity updated successfully:', data);
      return { data, pricingResult };
    },
    onSuccess: ({ pricingResult }) => {
      if (pricingResult.hasBulkPricing) {
        toast.success(`Quantity updated with bulk pricing applied: ${pricingResult.tierInfo}`);
      } else {
        toast.success('Line item quantity updated');
      }
      queryClient.invalidateQueries({ queryKey: ['estimateLineItems', estimateId] });
    },
    onError: (error) => {
      console.error('[useLineItemMutations] Failed to update quantity:', error);
      toast.error('Failed to update quantity');
    },
  });

  return {
    addLineItemMutation,
    removeLineItemMutation,
    updateQuantityMutation,
  };
};
